import { useEffect, useMemo, useState, type ReactNode } from 'react';

import { AuthGuard } from '../../app/AuthGuard';
import { customerCabinetApi, type CustomerProfileDto } from '../../features/customerCabinet/api';
import {
  CustomerCabinetLayout,
  navigateCustomer,
  type CustomerCabinetScreenId,
  type CustomerFigmaNodes,
} from '../../layouts/CustomerCabinet/CustomerCabinetLayout';
import { CarrierCabinetLayout } from '../../layouts/CarrierCabinet/CarrierCabinetLayout';
import { useLanguage } from '../../i18n/LanguageProvider';
import type { AppLanguage } from '../../i18n/locale';
import { useAuth } from '../../state/auth';
import { AccountActions, Stars } from '../../ui';
import { ProfilePhoneField } from '../../ui/ProfilePhoneField/ProfilePhoneField';
import { normalizeStarsRating } from '../../ui/Stars/rating';
import { CustomerPageHeading, hashNumber, initials, maskPhone } from './customerCabinetShared';
import './customer-cabinet.css';

export type CustomerProfileScreenId = Extract<CustomerCabinetScreenId, 'profile-internal' | 'profile-external'>;

const FIGMA_NODES: Record<CustomerProfileScreenId, CustomerFigmaNodes> = {
  'profile-internal': { '1920': '2680:41236', '1440': '2680:40859', '768': '2618:17345', '375': '2618:17528' },
  'profile-external': { '1920': '2680:41317', '1440': '2680:40939', '768': '2620:17345', '375': '2620:17498' },
};

const GEOMETRY: Record<CustomerProfileScreenId, {
  height: Record<'desktop' | 'tablet' | 'mobile', number>;
  offset: Record<'desktop' | 'tablet' | 'mobile', number>;
}> = {
  'profile-internal': { height: { desktop: 1441, tablet: 1201, mobile: 1189 }, offset: { desktop: 0, tablet: 33, mobile: 33 } },
  'profile-external': { height: { desktop: 1063, tablet: 764, mobile: 809 }, offset: { desktop: 0, tablet: 0, mobile: 0 } },
};

function ProfileLayout({ children, screen }: { children: ReactNode; screen: CustomerProfileScreenId }) {
  const { user } = useAuth();
  const isCarrier = user?.active_role === 'carrier';

  if (isCarrier) {
    return (
      <CarrierCabinetLayout figmaNode={FIGMA_NODES[screen] as any} screen="carrier-search">
        {children}
      </CarrierCabinetLayout>
    );
  }

  return (
    <CustomerCabinetLayout
      contentHeight={GEOMETRY[screen].height}
      figmaNode={FIGMA_NODES[screen]}
      screen={screen}
      topOffset={GEOMETRY[screen].offset}
    >
      {children}
    </CustomerCabinetLayout>
  );
}

function monthYear(value: string, language: AppLanguage) {
  return new Intl.DateTimeFormat(language === 'en' ? 'en-GB' : 'ru-RU', {
    month: 'long',
    year: 'numeric',
  }).format(new Date(value));
}

function publicName(profile: CustomerProfileDto, language: AppLanguage) {
  const parts = profile.display_name.trim().split(/\s+/).filter(Boolean);
  if (parts.length < 2) return parts[0] || (language === 'en' ? 'Customer' : 'Заказчик');
  return `${parts[0]} ${parts[1][0]}.`;
}

function reviewsCountLabel(count: number, language: AppLanguage) {
  if (language === 'en') return `based on ${count} ${count === 1 ? 'review' : 'reviews'}`;
  return `на основе ${count} ${count === 1 ? 'отзыва' : 'отзывов'}`;
}

function ProfileStat({ label, value }: { label: string; value: ReactNode }) {
  return <div className="customer-profile-stat"><strong>{value}</strong><span>{label}</span></div>;
}

function ProfileField({
  disabled = false,
  label,
  name,
  onBlur,
  onChange,
  value,
}: {
  disabled?: boolean;
  label: string;
  name: string;
  onBlur?: () => void;
  onChange?: (value: string) => void;
  value: string;
}) {
  return (
    <label className={`customer-profile-field is-${name}`}>
      <span>{label}</span>
      <input disabled={disabled} value={value} onBlur={onBlur} onChange={(event) => onChange?.(event.target.value)} />
    </label>
  );
}

function InternalProfileView() {
  const auth = useAuth();
  const { language } = useLanguage();
  const [profile, setProfile] = useState<CustomerProfileDto | null>(null);
  const [draft, setDraft] = useState({ first_name: '', last_name: '', city: '' });
  const [emailState, setEmailState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [state, setState] = useState<'loading' | 'ready' | 'saving' | 'error'>('loading');

  useEffect(() => {
    let active = true;
    customerCabinetApi.profile().then((next) => {
      if (!active) return;
      setProfile(next);
      setDraft({ first_name: next.first_name ?? '', last_name: next.last_name ?? '', city: next.city ?? '' });
      setState('ready');
    }).catch(() => { if (active) setState('error'); });
    return () => { active = false; };
  }, []);

  async function save(fields: Record<string, unknown>) {
    if (!profile) return;
    setState('saving');
    try {
      const next = await customerCabinetApi.updateProfile(fields);
      setProfile(next);
      setDraft({ first_name: next.first_name ?? '', last_name: next.last_name ?? '', city: next.city ?? '' });
      setState('ready');
    } catch {
      setState('error');
    }
  }

  async function sendEmailVerification() {
    setEmailState('sending');
    try {
      await customerCabinetApi.sendEmailVerification();
      setEmailState('sent');
      await auth.refresh();
    } catch {
      setEmailState('error');
    }
  }

  function toggle(field: 'push_enabled' | 'sms_active_transport' | 'email_marketing') {
    if (!profile) return;
    void save({ [field]: !profile[field] });
  }

  async function deleteAccount() {
    const confirmed = window.confirm(
      'Удалить аккаунт безвозвратно? Личные данные, файлы, рейтинг и доступ к истории будут удалены.',
    );
    if (!confirmed) return;
    try {
      await auth.deleteAccount();
      window.location.hash = 'landing';
    } catch {
      setState('error');
      window.alert('Не удалось удалить аккаунт. Повторите попытку или обратитесь в поддержку.');
    }
  }

  if (!profile) return <ProfileLayout screen="profile-internal"><div className="customer-profile-internal" data-state={state} /></ProfileLayout>;

  return (
    <ProfileLayout screen="profile-internal">
      <div className="customer-profile-internal" data-state={state}>
        <CustomerPageHeading
          className="customer-profile-title"
          figmaNode="2680:40863"
          title="Мой профиль"
          subtitle="Здесь редактируете личные данные. Перевозчики увидят только имя и оценку — телефон, адрес и email скрыты."
        />

        <section className="customer-profile-head" data-figma-node="2680:40868">
          <div className="customer-profile-avatar">{initials(profile.display_name)}</div>
          <div className="customer-profile-head__copy">
            <strong>{profile.display_name}</strong>
            <span>
              {maskPhone(profile.phone)} · {language === 'en' ? 'on the platform since' : 'на платформе с'}{' '}
              {monthYear(profile.created_at, language)}
            </span>
          </div>
          <button className="customer-profile-secondary" type="button" onClick={() => navigateCustomer('profile-external')}>Посмотреть, как видит перевозчик →</button>
        </section>

        <section className="customer-profile-stats" data-figma-node="2680:40876">
          <ProfileStat label="Всего заявок" value={profile.total_requests_count} />
          <ProfileStat label="Завершённых перевозок" value={profile.completed_requests_count} />
          <ProfileStat label="Рейтинг как заказчика" value={<>★ {Number(profile.rating).toFixed(1)}</>} />
        </section>

        <section className="customer-profile-card customer-profile-contacts" data-figma-node="2680:40888">
          <h2>Контактные данные</h2>
          <div className="customer-profile-contacts__grid">
            <ProfileField label="Имя" name="first-name" value={draft.first_name} onChange={(first_name) => setDraft((current) => ({ ...current, first_name }))} onBlur={() => void save({ first_name: draft.first_name })} />
            <ProfileField label="Фамилия" name="last-name" value={draft.last_name} onChange={(last_name) => setDraft((current) => ({ ...current, last_name }))} onBlur={() => void save({ last_name: draft.last_name })} />
            <ProfileField label="Город" name="city" value={draft.city} onChange={(city) => setDraft((current) => ({ ...current, city }))} onBlur={() => void save({ city: draft.city })} />
            <div className="customer-profile-phone">
              <ProfilePhoneField value={profile.phone ?? ''} onVerified={(phone) => setProfile((current) => current ? { ...current, phone } : current)} />
            </div>
            <div className="customer-profile-email">
              <span className="customer-profile-email__label">Email <i>{auth.user?.email_verified_at ? 'подтверждён' : 'не подтверждён'}</i></span>
              <div><input disabled value={profile.email ?? auth.user?.email ?? ''} /><button disabled={Boolean(auth.user?.email_verified_at) || emailState === 'sending'} type="button" onClick={() => void sendEmailVerification()}>{auth.user?.email_verified_at ? 'Подтверждён' : emailState === 'sent' ? 'Отправлено' : 'Подтвердить'}</button></div>
            </div>
          </div>
        </section>

        <AccountActions
          onDeleteAccount={() => void deleteAccount()}
          onSignOut={() => void auth.signOut().then(() => { window.location.hash = 'landing'; })}
        />
      </div>
    </ProfileLayout>
  );
}

function ExternalProfileView() {
  const requestedId = hashNumber('customer') ?? hashNumber('id');
  const [profile, setProfile] = useState<CustomerProfileDto | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');
  const { user } = useAuth();
  const { language } = useLanguage();
  const isCarrier = user?.active_role === 'carrier';

  useEffect(() => {
    let active = true;
    const load = async () => {
      let next: CustomerProfileDto | null = null;
      if (requestedId) {
        next = await customerCabinetApi.publicProfile(requestedId);
      } else if (user?.active_role === 'customer') {
        const own = await customerCabinetApi.profile();
        next = await customerCabinetApi.publicProfile(own.id);
      }
      if (!active) return;
      if (next) {
        setProfile(next);
        setState('ready');
      } else {
        setState('error');
      }
    };
    load().catch(() => { if (active) setState('error'); });
    return () => { active = false; };
  }, [requestedId, user?.active_role]);

  const visibleRating = useMemo(() => {
    const value = Number(profile?.rating ?? 0);
    return profile?.reviews.length && Number.isFinite(value) ? Math.max(0, Math.min(5, value)) : 0;
  }, [profile?.rating, profile?.reviews.length]);

  return (
    <ProfileLayout screen="profile-external">
      <div className="customer-profile-external" data-state={state}>
        {profile ? (
          <>
            {!isCarrier ? (
              <div className="customer-profile-preview">👁 Это превью — так ваш профиль выглядит для перевозчика. Личные данные скрыты до принятия отклика.</div>
            ) : null}
            <CustomerPageHeading
              className="customer-profile-public-title"
              figmaNode="2680:40944"
              title={publicName(profile, language)}
              subtitle={language === 'en'
                ? `Customer · on LOVO since ${monthYear(profile.created_at, language)}`
                : `Заказчик · на LOVO с ${monthYear(profile.created_at, language)}`}
            />
            <section className="customer-profile-public-head" data-figma-node="2680:40952">
              <div className="customer-profile-avatar is-large">{initials(profile.display_name)}</div>
              <div>
                <p>
                  <strong>{visibleRating.toFixed(1)}</strong>
                  <Stars mode="Light" rating={normalizeStarsRating(visibleRating)} />
                </p>
                <button type="button" onClick={() => document.querySelector('.customer-profile-reviews')?.scrollIntoView({ behavior: 'smooth' })}>
                  {reviewsCountLabel(profile.reviews.length, language)}
                </button>
                {profile.city ? <span>⌖ {profile.city}</span> : null}
              </div>
            </section>
            <section className="customer-profile-stats is-public" data-figma-node="2680:40963">
              <ProfileStat label="завершённых перевозок" value={profile.completed_requests_count} />
              <ProfileStat label="оплат вовремя" value={`${profile.payment_on_time_percent}%`} />
              <ProfileStat label="подтверждённых заявок" value={`${profile.confirmed_requests_percent}%`} />
            </section>
            <section className="customer-profile-card customer-profile-reviews" data-figma-node="2680:40973">
              <h2>Отзывы перевозчиков</h2>
              {profile.reviews.length ? (
                <div>
                  {profile.reviews.map((review) => (
                    <article key={review.id}>
                      <span>{initials(review.author_name)}</span>
                      <div><p><strong>{review.author_name}</strong><Stars mode="Light" rating={normalizeStarsRating(review.rating)} /></p><blockquote>«{review.comment}»</blockquote></div>
                      <time dateTime={review.created_at}>{monthYear(review.created_at, language)}</time>
                    </article>
                  ))}
                </div>
              ) : (
                <p className="customer-profile-reviews__empty">
                  {language === 'en' ? 'No carrier reviews yet' : 'Отзывы пока что отсутствуют'}
                </p>
              )}
            </section>
            <button
              className="customer-profile-secondary customer-profile-back"
              type="button"
              onClick={() => {
                if (isCarrier) {
                  window.history.back();
                } else {
                  navigateCustomer('profile-internal');
                }
              }}
            >
              {isCarrier ? '← Назад' : '← Вернуться во внутренний вид'}
            </button>
          </>
        ) : null}
      </div>
    </ProfileLayout>
  );
}

export function CustomerProfileScreen({ screen }: { screen: CustomerProfileScreenId }) {
  if (screen === 'profile-external') {
    return <ExternalProfileView />;
  }
  return (
    <AuthGuard allow={['customer']}>
      <InternalProfileView />
    </AuthGuard>
  );
}
